import { Box, CardContent, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { ReviewSummaryDialog } from 'apps/wrapper/src/components/shared/patient-feedback/dialogs/review-summary/review-summary';
import { SurveyResultsDialog } from 'apps/wrapper/src/components/shared/patient-feedback/dialogs/survey-results/survey-results';
import PatientFeedbackService from 'apps/wrapper/src/services/patient-feedback-service/patient-feedback-service';
import {
  FeedbackType,
  PatientFeedback
} from 'apps/wrapper/src/services/patient-feedback-service/patient-feedback-service-types';
import moment from 'moment';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { usePatientProfile } from '../../../../../../hooks/use-patient-profile';
import { useCreateReviewSurvey, useIsReviewSurveyEnabled } from '../../../../../../hooks/use-review-survey';
import { NoDataComponent } from '../../../../../shared/grid/no-data';
import { StarRating } from '../../../../../shared/star-rating/star-rating';
import { Button, Card, CardTitle, Table } from '../../patient-profile-page.styles';
import FeedbackTableSkeleton from './feedback.skeleton';
import { Container, FeedbackButton, SurveyRating } from './feedback.styles';

export const feedbackTypeToTitle = {
  [FeedbackType.Review]: 'Review',
  [FeedbackType.Survey]: 'Survey'
};

export const FeedbackView: FC = () => {
  const { showError } = useNotification();
  const { contactDetails, contactLocation, patientInfo, locationId, loggedInUserLocation } = usePatientProfile();
  const { request } = useCreateReviewSurvey({ contactDetails, locationId });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSurvey, setIsLoadingSurvey] = useState(false);
  const [isLoadingReview, setIsLoadingReview] = useState(false);
  const [patientFeedback, setPatientFeeedback] = useState<PatientFeedback[]>([]);
  const { config } = useConfig();
  const oidc = useOidcWithAdmin();
  const [selectedReviewId, setSelectedReviewId] = useState<number | null>(null);
  const [selectedSurveyId, setSelectedSurveyId] = useState<number | null>(null);

  const patientFeedbackService = useMemo(
    () => PatientFeedbackService.getInstance(config, oidc.oidcService),
    [config, oidc]
  );

  const isReviewSurveyEnabled = useIsReviewSurveyEnabled({
    contactDetails,
    contactLocation,
    patientInfo,
    loggedInUserLocation
  });

  const loadPatientFeedback = useCallback(async () => {
    setIsLoading(true);

    try {
      const feedback = await patientFeedbackService.getPatientFeedback(contactDetails.id, locationId);

      setPatientFeeedback(feedback);
    } catch {
      showError('Unable to load patient feedback.');
    } finally {
      setIsLoading(false);
    }
  }, [contactDetails, locationId]);

  useEffect(() => {
    loadPatientFeedback();
  }, []);

  useEffect(() => {
    if (!patientFeedback || patientFeedback.length === 0) return;

    const queryParams = new URLSearchParams(location.search);
    const reviewIdParam = queryParams.get('reviewId');

    if (reviewIdParam) {
      setSelectedReviewId(parseInt(reviewIdParam));
    }
  }, [patientFeedback, location]);

  const handleSendSurveyClick = async () => {
    setIsLoadingSurvey(true);
    await request(FeedbackType.Survey);
    setIsLoadingSurvey(false);
  };

  const handleSendReviewClick = async () => {
    setIsLoadingReview(true);
    await request(FeedbackType.Review);
    setIsLoadingReview(false);
  };

  const handleFeedbackItemClick = (feedback: PatientFeedback) => {
    if (feedback.feedbackType === FeedbackType.Review) {
      setSelectedReviewId(feedback.idPatientFeedback);
    } else {
      setSelectedSurveyId(feedback.idPatientFeedback);
    }
  };

  return (
    <>
      <Container>
        <Card>
          <CardContent>
            <CardTitle sx={{ display: 'flex', justifyContent: 'space-between', padding: '10px', width: 'auto' }}>
              <Box>{contactDetails?.firstName}&apos;s Feedback</Box>
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Button
                  size="small"
                  loading={isLoadingSurvey}
                  disabled={!isReviewSurveyEnabled || isLoading || isLoadingReview}
                  onClick={handleSendSurveyClick}
                  variant="contained"
                  disableElevation
                >
                  Send Survey
                </Button>
                <Button
                  size="small"
                  loading={isLoadingReview}
                  disabled={!isReviewSurveyEnabled || isLoading || isLoadingSurvey}
                  onClick={handleSendReviewClick}
                  variant="contained"
                  disableElevation
                  sx={{
                    marginLeft: '10px'
                  }}
                >
                  Send Review
                </Button>
              </Box>
            </CardTitle>
            {isLoading && <FeedbackTableSkeleton />}
            {!isLoading && patientFeedback.length === 0 && <NoDataComponent />}
            {!isLoading && patientFeedback.length > 0 && (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '15%' }}>Date</TableCell>
                    <TableCell>Response</TableCell>
                    <TableCell sx={{ width: '15%' }}>Type</TableCell>
                    <TableCell sx={{ width: '15%' }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {patientFeedback.map((item) => (
                    <TableRow key={item.idPatientFeedback}>
                      <TableCell style={{ width: '15%', paddingRight: '15px', color: 'black' }}>
                        {item.date ? moment(item.date).format('MM/DD/YYYY') : null}
                      </TableCell>
                      <TableCell style={{ paddingRight: '15px', color: '#0675DD' }}>
                        <FeedbackButton type="button" onClick={() => handleFeedbackItemClick(item)}>
                          {item.message}
                        </FeedbackButton>
                      </TableCell>
                      <TableCell style={{ width: '15%', color: 'black' }}>
                        {feedbackTypeToTitle[item.feedbackType]}
                      </TableCell>
                      <TableCell style={{ width: '15%' }}>
                        {item.feedbackType === FeedbackType.Review && <StarRating rating={item.ratingScore} />}
                        {item.feedbackType === FeedbackType.Survey && (
                          <SurveyRating score={item.ratingScore}>{item.ratingScore}%</SurveyRating>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </Container>
      {selectedReviewId && (
        <ReviewSummaryDialog
          reviewId={selectedReviewId}
          open={selectedReviewId != null}
          onClose={() => setSelectedReviewId(null)}
        />
      )}
      {selectedSurveyId && (
        <SurveyResultsDialog
          surveyId={selectedSurveyId}
          open={selectedSurveyId != null}
          onClose={() => setSelectedSurveyId(null)}
        />
      )}
    </>
  );
};
